import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import {
  type DeploymentTarget,
  type ModelStatus,
  type ModelTaskType,
  useCreateFoundryModel,
} from "@via/pages/cortex/foundry-models/foundry-models.api.tsx";
import { requiredValidation } from "@via/utils/form-validators.tsx";
import { SelectOption } from "@via/utils/interfaces.tsx";
import { type FC, useState } from "react";
import { useForm } from "react-hook-form";

interface CreateFoundryModelFormProps {
  setOpen: (open: boolean) => void;
}

interface CreateFoundryModelFormValues {
  name: string;
  version: string;
  status: ModelStatus;
  taskType: ModelTaskType;
  owner: string;
  deploymentTarget?: DeploymentTarget;
}

const CreateFoundryModelForm: FC<CreateFoundryModelFormProps> = ({
  setOpen,
}) => {
  const { mutate: createFoundryModel } = useCreateFoundryModel();

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<CreateFoundryModelFormValues>({
    mode: "onChange",
    defaultValues: {
      name: "",
      version: "v1.0.0",
      status: "Training",
      taskType: "Classification",
      owner: "",
      deploymentTarget: undefined,
    },
  });

  const selectedStatus = watch("status");
  const showDeploymentFields =
    selectedStatus === "Deployed" ||
    selectedStatus === "Production" ||
    selectedStatus === "Testing";

  const [computeType, setComputeType] = useState<"GPU" | "CPU">("GPU");
  const [region, setRegion] = useState("");
  const [endpointUrl, setEndpointUrl] = useState("");
  const [containerVersion, setContainerVersion] = useState("");

  const onSubmit = handleSubmit((data: CreateFoundryModelFormValues) => {
    const payload = {
      ...data,
      deploymentTarget: showDeploymentFields
        ? {
            computeType,
            region,
            endpointUrl: endpointUrl || undefined,
            containerVersion: containerVersion || undefined,
          }
        : undefined,
    };
    createFoundryModel(payload);
    setOpen(false);
  });

  const statusOptions: SelectOption[] = [
    { label: "Training", value: "Training" },
    { label: "Deployed", value: "Deployed" },
    { label: "Testing", value: "Testing" },
    { label: "Production", value: "Production" },
    { label: "Archived", value: "Archived" },
  ];

  const taskTypeOptions: SelectOption[] = [
    { label: "Classification", value: "Classification" },
    { label: "Regression", value: "Regression" },
    { label: "Embedding", value: "Embedding" },
    { label: "Sequence model", value: "Sequence model" },
    { label: "LLM / text generation", value: "LLM / text generation" },
    { label: "Image model", value: "Image model" },
    { label: "QC flag prediction", value: "QC flag prediction" },
  ];

  const computeTypeOptions: SelectOption[] = [
    { label: "GPU", value: "GPU" },
    { label: "CPU", value: "CPU" },
  ];

  const regionOptions: SelectOption[] = [
    { label: "us-central1", value: "us-central1" },
    { label: "us-east1", value: "us-east1" },
    { label: "us-west1", value: "us-west1" },
    { label: "europe-west1", value: "europe-west1" },
    { label: "asia-east1", value: "asia-east1" },
  ];

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        onSubmit(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <h3 className="text-base font-semibold leading-6 text-gray-900">
            Register Foundry Model
          </h3>
          <div className="mt-1">
            <p className="text-sm text-gray-500">
              Get started by filling in the information below to register your
              new Foundry Model.
            </p>
          </div>
        </div>
        <div className="divide-y divide-gray-200 px-4 sm:px-6">
          <div className="space-y-6 pb-5 pt-6">
            <div>
              <FormInputComponent
                control={control}
                name="name"
                label="Model Name"
                showRequiredField={true}
                placeholder="e.g., rna_seq_qc_classifier_v1"
                validations={requiredValidation("Model Name")}
              />
            </div>

            <div>
              <FormInputComponent
                control={control}
                name="version"
                label="Version"
                showRequiredField={true}
                placeholder="e.g., v1.4.2"
                validations={requiredValidation("Version")}
              />
            </div>

            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Status / Stage <span className="text-red-500">*</span>
              </label>
              <div className="mt-2">
                <SelectComponent
                  control={control}
                  name="status"
                  rules={requiredValidation("Status")}
                  options={statusOptions}
                  placeholder="Select status"
                  enableClear={false}
                />
                {errors.status && (
                  <span className="text-xs text-rose-500">
                    {errors.status.message || "This field is required"}
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Type / Task <span className="text-red-500">*</span>
              </label>
              <div className="mt-2">
                <SelectComponent
                  control={control}
                  name="taskType"
                  rules={requiredValidation("Task Type")}
                  options={taskTypeOptions}
                  placeholder="Select task type"
                  enableClear={false}
                />
                {errors.taskType && (
                  <span className="text-xs text-rose-500">
                    {errors.taskType.message || "This field is required"}
                  </span>
                )}
              </div>
            </div>

            <div>
              <FormInputComponent
                control={control}
                name="owner"
                label="Owner / Project"
                showRequiredField={true}
                placeholder="e.g., Bioinformatics Team"
                validations={requiredValidation("Owner / Project")}
              />
            </div>

            {showDeploymentFields && (
              <>
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-4">
                    Deployment Target / Endpoint
                  </h4>
                </div>

                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Compute Type <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2">
                    <SelectComponent
                      name="compute-type"
                      placeholder="Select compute type"
                      options={computeTypeOptions}
                      onChange={selected => {
                        if (
                          selected &&
                          !Array.isArray(selected) &&
                          "value" in selected
                        ) {
                          setComputeType(selected.value as "GPU" | "CPU");
                        }
                      }}
                      value={
                        computeTypeOptions.find(
                          opt => opt.value === computeType
                        ) || null
                      }
                      enableClear={false}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Region <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2">
                    <SelectComponent
                      name="region"
                      placeholder="Select region"
                      options={regionOptions}
                      onChange={selected => {
                        if (
                          selected &&
                          !Array.isArray(selected) &&
                          "value" in selected
                        ) {
                          setRegion(selected.value as string);
                        }
                      }}
                      value={
                        regionOptions.find(opt => opt.value === region) || null
                      }
                      enableClear={false}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Endpoint URL
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      placeholder="e.g., https://api.viafoundry.com/models/..."
                      value={endpointUrl}
                      onChange={e => setEndpointUrl(e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Inference Container Version
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      placeholder="e.g., v2.1.0"
                      value={containerVersion}
                      onChange={e => setContainerVersion(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="border-t border-gray-200 px-4 py-4 sm:px-6">
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
            Cancel
          </button>
          <button
            type="submit"
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
            Register Model
          </button>
        </div>
      </div>
    </form>
  );
};

export default CreateFoundryModelForm;
